// Fase 9 (BI) + Fase 10 (Administração) — mocked prototype
import React from 'react';
import { BarChart3, TrendingUp, Zap, Award, Map, Grid3x3, Trophy, Download, Filter, Settings, ShieldCheck, ScrollText, Database, Plug, Globe, Webhook, CloudUpload, Bell, Building2, ShieldAlert, KeyRound, Server, Activity } from 'lucide-react';
import { MockPageShell, MockKpi, MockBar, MockPie, MockLine, MockArea, ChartCard, MockToolbar, MockTable, StatusBadge } from '../../components/MockUI';
import { genPie, genSeries, chartMonths } from '../../lib/mockData';
import { Button } from '../../components/ui/button';

// ============ BI (Fase 9) ============

const bcBI = (l) => [{ label: 'Business Intelligence', to: '/bi' }, { label: l }];

export function BIDashboard() {
  return <MockPageShell title="Dashboard Executivo" subtitle="Visão consolidada da operação" breadcrumbs={[{ label: 'Business Intelligence' }]}
    kpis={[
      <MockKpi key="1" icon={TrendingUp} label="Receita mês" value="R$ 1.24M" tone="success" />,
      <MockKpi key="2" icon={BarChart3} label="Lucro operacional" value="R$ 272K" tone="success" />,
      <MockKpi key="3" icon={Zap} label="Custo por km" value="R$ 4,18" />,
      <MockKpi key="4" icon={Award} label="NPS clientes" value="72" tone="success" />,
    ]}
    charts={[
      <ChartCard key="1" title="Receita × Lucro (6 meses)"><MockLine data={chartMonths.map(m => ({ name: m, value: 800 + Math.random() * 500 }))} /></ChartCard>,
      <ChartCard key="2" title="Mix de receita"><MockPie data={genPie(['Contratos públicos', 'Fretamento contínuo', 'Turismo', 'Escolar', 'Eventual'])} /></ChartCard>,
    ]}
    extra={<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <ChartCard title="Frota em operação"><MockBar data={chartMonths.map(m => ({ name: m, value: 42 + Math.random() * 8 | 0 }))} /></ChartCard>,
      <ChartCard title="Custo operacional"><MockArea data={genSeries(600, 200)} color="#F59E0B" /></ChartCard>,
      <ChartCard title="Utilização de frota (%)"><MockLine data={genSeries(78, 12)} color="#8B5CF6" /></ChartCard>,
    </div>}
  />;
}

export function KPIs() {
  const kpis = [
    { name: 'Custo por km rodado', value: 'R$ 4,18', trend: '-2,3%', tone: 'success' },
    { name: 'Consumo médio (km/L)', value: '3,42', trend: '+1,8%', tone: 'success' },
    { name: 'Disponibilidade da frota', value: '87,4%', trend: '+0,6%', tone: 'success' },
    { name: 'Utilização da frota', value: '78,2%', trend: '+2,1%', tone: 'success' },
    { name: 'MTBF (h entre falhas)', value: '184h', trend: '-5,4%', tone: 'critical' },
    { name: 'MTTR (h para reparo)', value: '6,2h', trend: '+8,1%', tone: 'critical' },
    { name: 'Tempo médio de viagem', value: '54min', trend: '-3,2%', tone: 'success' },
    { name: 'Ocupação média', value: '82,4%', trend: '+1,4%', tone: 'success' },
    { name: 'Satisfação clientes', value: '4.6/5', trend: '+0,2', tone: 'success' },
    { name: 'Reclamações (30d)', value: '8', trend: '-25%', tone: 'success' },
    { name: 'Rotatividade RH', value: '8,4%', trend: '+1,1%', tone: 'critical' },
    { name: 'Absenteísmo', value: '2,1%', trend: '-0,3%', tone: 'success' },
  ];
  return <MockPageShell title="KPIs Operacionais e Estratégicos" breadcrumbs={bcBI('KPIs')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">{kpis.map((k, i) => (
      <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
        <p className="text-[11px] uppercase tracking-wide text-neutral-500">{k.name}</p>
        <p className="text-2xl font-bold mt-1">{k.value}</p>
        <p className={`text-xs mt-1 ${k.tone === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{k.trend} vs. mês anterior</p>
      </div>
    ))}</div>}
  />;
}

export function BIGraficos() {
  return <MockPageShell title="Análises Gráficas" breadcrumbs={bcBI('Gráficos')}
    charts={[
      <ChartCard key="1" title="Receita por segmento"><MockBar data={genPie(['Público', 'Contínuo', 'Turismo', 'Escolar'], 200, 400)} color="#3B82F6" /></ChartCard>,
      <ChartCard key="2" title="Custo por categoria"><MockPie data={genPie(['Combustível', 'Manutenção', 'Folha', 'Impostos', 'Outros'])} /></ChartCard>,
      <ChartCard key="3" title="Evolução da margem"><MockLine data={genSeries(15, 8)} color="#10B981" /></ChartCard>,
      <ChartCard key="4" title="Viagens por mês"><MockArea data={genSeries(1400, 400)} color="#8B5CF6" /></ChartCard>,
    ]}
  />;
}

export function Comparativos() {
  const rows = [
    { metric: 'Receita', current: 'R$ 1.240K', previous: 'R$ 1.080K', variation: '+14,8%' },
    { metric: 'Custo operacional', current: 'R$ 620K', previous: 'R$ 590K', variation: '+5,1%' },
    { metric: 'Lucro líquido', current: 'R$ 228K', previous: 'R$ 168K', variation: '+35,7%' },
    { metric: 'Viagens realizadas', current: '1.842', previous: '1.706', variation: '+8,0%' },
    { metric: 'Km rodados', current: '148.2K', previous: '140.1K', variation: '+5,8%' },
    { metric: 'Custo por km', current: 'R$ 4,18', previous: 'R$ 4,21', variation: '-0,7%' },
  ];
  return <MockPageShell title="Comparativos" subtitle="Período atual vs. período anterior" breadcrumbs={bcBI('Comparativos')}
    table={<MockTable columns={[{ key: 'metric', label: 'Métrica' }, { key: 'current', label: 'Atual', align: 'right' }, { key: 'previous', label: 'Anterior', align: 'right' }, { key: 'variation', label: 'Variação', align: 'right' }]} rows={rows} />}
  />;
}

export function Heatmaps() {
  const days = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
  const hours = ['06h', '08h', '10h', '12h', '14h', '16h', '18h', '20h'];
  return <MockPageShell title="Heatmap de Viagens" subtitle="Distribuição por dia/hora" breadcrumbs={bcBI('Heatmaps')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <div className="overflow-x-auto"><table className="text-xs">
        <thead><tr><th></th>{hours.map(h => <th key={h} className="px-3 py-2 text-center text-neutral-500">{h}</th>)}</tr></thead>
        <tbody>{days.map(d => (
          <tr key={d}><th className="px-3 py-1 text-right text-neutral-500 font-normal">{d}</th>
            {hours.map(h => {
              const v = Math.random() * 100 | 0;
              const alpha = Math.min(1, v / 100);
              return <td key={h} className="p-0"><div className="w-12 h-8 mx-0.5 my-0.5 rounded" style={{ backgroundColor: `rgba(197, 36, 41, ${alpha})` }} title={`${v} viagens`} /></td>;
            })}
          </tr>
        ))}</tbody>
      </table></div>
      <div className="flex items-center gap-2 mt-4 text-xs">
        <span className="text-neutral-500">Menos</span>
        {[0.1, 0.3, 0.5, 0.7, 0.9].map(a => <div key={a} className="w-6 h-3 rounded" style={{ backgroundColor: `rgba(197, 36, 41, ${a})` }} />)}
        <span className="text-neutral-500">Mais</span>
      </div>
    </div>}
  />;
}

export function Rankings() {
  const drivers = [
    { pos: 1, name: 'Carlos Silva', metric: '2.145 km', score: 98 },
    { pos: 2, name: 'João Ferreira', metric: '2.089 km', score: 96 },
    { pos: 3, name: 'Ana Paula Souza', metric: '1.987 km', score: 94 },
    { pos: 4, name: 'Roberto Almeida', metric: '1.842 km', score: 91 },
    { pos: 5, name: 'Pedro Santos', metric: '1.756 km', score: 89 },
  ];
  const clients = [
    { pos: 1, name: 'Prefeitura de Canoas', metric: 'R$ 128K', score: 100 },
    { pos: 2, name: 'UFRGS', metric: 'R$ 45K', score: 92 },
    { pos: 3, name: 'Vinícola Aurora', metric: 'R$ 18,5K', score: 88 },
  ];
  return <MockPageShell title="Rankings" breadcrumbs={bcBI('Rankings')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ChartCard title="Top Motoristas" subtitle="Por km rodados no mês">
        <ul className="space-y-2 mt-2">{drivers.map(d => (
          <li key={d.pos} className="flex items-center gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
            <span className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${d.pos === 1 ? 'bg-amber-100 text-amber-800' : d.pos === 2 ? 'bg-neutral-300 text-neutral-700' : d.pos === 3 ? 'bg-orange-100 text-orange-800' : 'bg-neutral-100 text-neutral-600'}`}>{d.pos}</span>
            <span className="flex-1 font-medium text-sm">{d.name}</span>
            <span className="font-mono text-xs">{d.metric}</span>
          </li>
        ))}</ul>
      </ChartCard>
      <ChartCard title="Top Clientes" subtitle="Por faturamento no mês">
        <ul className="space-y-2 mt-2">{clients.map(c => (
          <li key={c.pos} className="flex items-center gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
            <span className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${c.pos === 1 ? 'bg-amber-100 text-amber-800' : c.pos === 2 ? 'bg-neutral-300 text-neutral-700' : 'bg-orange-100 text-orange-800'}`}>{c.pos}</span>
            <span className="flex-1 font-medium text-sm">{c.name}</span>
            <span className="font-mono text-xs">{c.metric}</span>
          </li>
        ))}</ul>
      </ChartCard>
    </div>}
  />;
}

export function BIRelatorios() {
  const items = ['Executive summary mensal', 'Rentabilidade por contrato', 'Análise de custos operacionais', 'Utilização da frota', 'KPIs consolidados', 'Rentabilidade por rota'];
  return <MockPageShell title="Relatórios Executivos" breadcrumbs={bcBI('Relatórios')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{items.map((r, i) => (
      <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 flex items-center gap-3 card-hover">
        <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><BarChart3 className="h-5 w-5" /></div>
        <div className="flex-1"><p className="font-medium text-sm">{r}</p></div>
        <Button size="sm" variant="outline">Gerar</Button>
      </div>
    ))}</div>}
  />;
}

export function Exportacoes() {
  const rows = [
    { name: 'Fatura fevereiro 2026.xlsx', kind: 'Excel', size: '1.2 MB', date: '10/02/2026 09:20', user: 'Fernanda Lima' },
    { name: 'DRE gerencial.pdf', kind: 'PDF', size: '480 KB', date: '09/02/2026 15:03', user: 'Fernanda Lima' },
    { name: 'Frota completa.csv', kind: 'CSV', size: '82 KB', date: '08/02/2026 11:15', user: 'Marcelo Rodrigues' },
  ];
  return <MockPageShell title="Exportações" breadcrumbs={bcBI('Exportações')}
    table={<MockTable columns={[{ key: 'name', label: 'Arquivo' }, { key: 'kind', label: 'Tipo' }, { key: 'size', label: 'Tamanho', align: 'right' }, { key: 'date', label: 'Gerado em' }, { key: 'user', label: 'Por' }]} rows={rows} />}
  />;
}

// ============ Fase 10 — Administração ============

const bcA = (l) => [{ label: 'Administração', to: '/admin' }, { label: l }];

export function AdminConfig() {
  const sections = [
    { title: 'Empresa', description: 'Nome, CNPJ, endereço e logo', icon: Building2 },
    { title: 'Preferências', description: 'Locale, fuso horário, moeda', icon: Settings },
    { title: 'Numeração de documentos', description: 'Prefixos e sequências', icon: ScrollText },
    { title: 'E-mails automáticos', description: 'Templates de notificações', icon: Bell },
    { title: 'Personalização visual', description: 'Cores, logo, layout', icon: Settings },
    { title: 'Módulos ativos', description: 'Habilitar/desabilitar módulos', icon: Grid3x3 },
  ];
  return <MockPageShell title="Configurações Gerais" breadcrumbs={[{ label: 'Administração' }]}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{sections.map((s, i) => (
      <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 card-hover cursor-pointer">
        <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center mb-3"><s.icon className="h-5 w-5" /></div>
        <h3 className="font-semibold">{s.title}</h3>
        <p className="text-xs text-neutral-500 mt-1">{s.description}</p>
      </div>
    ))}</div>}
  />;
}

export function AdminLogs() {
  const rows = Array.from({ length: 12 }, (_, i) => ({
    date: `10/02/2026 ${(9 + i).toString().padStart(2, '0')}:${(Math.random() * 59 | 0).toString().padStart(2, '0')}`,
    user: ['Fernanda Lima', 'Marcelo Rodrigues', 'Ricardo Bastos', 'Admin', 'Ana Paula Souza'][i % 5],
    module: ['Frota', 'Manutenção', 'Financeiro', 'RH', 'Auth'][i % 5],
    action: ['LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT'][i % 5],
    ref: `${['veh', 'os', 'cli', 'emp', 'usr'][i % 5]}_${i + 100}`,
    ip: `192.168.${(Math.random() * 255) | 0}.${(Math.random() * 255) | 0}`,
  }));
  return <MockPageShell title="Logs de Atividade" breadcrumbs={bcA('Logs')}
    extra={<MockToolbar filters={<Button variant="outline"><Filter className="h-4 w-4 mr-2" />Filtros</Button>} />}
    table={<MockTable columns={[{ key: 'date', label: 'Data', mono: true }, { key: 'user', label: 'Usuário' }, { key: 'module', label: 'Módulo' }, { key: 'action', label: 'Ação', status: true }, { key: 'ref', label: 'Referência', mono: true }, { key: 'ip', label: 'IP', mono: true }]} rows={rows} statusMap={{ LOGIN: 'blue', CREATE: 'green', UPDATE: 'amber', DELETE: 'red', EXPORT: 'purple' }} />}
  />;
}

export function Auditoria() {
  return <MockPageShell title="Auditoria" subtitle="Trilha de alterações críticas" breadcrumbs={bcA('Auditoria')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <h3 className="font-semibold mb-3">Alterações relevantes</h3>
      <ol className="relative border-l-2 border-neutral-200 dark:border-neutral-800 ml-2 space-y-3">
        {[
          { title: 'Permissões alteradas — perfil Financeiro', date: '10/02 10:22', user: 'Admin', tone: 'bg-red-500' },
          { title: 'Novo usuário cadastrado — João Ferreira', date: '10/02 09:15', user: 'Ricardo Bastos', tone: 'bg-emerald-500' },
          { title: 'Config. de e-mail SMTP atualizada', date: '09/02 16:40', user: 'Admin', tone: 'bg-blue-500' },
          { title: 'Backup manual gerado', date: '09/02 08:00', user: 'Sistema', tone: 'bg-neutral-500' },
        ].map((e, i) => (
          <li key={i} className="pl-6 relative">
            <span className={`absolute -left-[9px] top-1.5 h-4 w-4 rounded-full border-2 border-white dark:border-neutral-900 ${e.tone}`} />
            <div className="text-sm font-medium">{e.title}</div>
            <div className="text-xs text-neutral-500">{e.date} • {e.user}</div>
          </li>
        ))}
      </ol>
    </div>}
  />;
}

export function AdminSeguranca() {
  return <MockPageShell title="Segurança" breadcrumbs={bcA('Segurança')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
        { title: 'Política de senhas', description: 'Mínimo 8 caracteres, 1 número, 1 especial', icon: KeyRound },
        { title: 'Autenticação em 2 fatores (MFA)', description: 'Habilitado para administradores', icon: ShieldCheck },
        { title: 'Sessão / Timeout', description: 'Inatividade máxima de 30 minutos', icon: ShieldAlert },
        { title: 'IP allowlist', description: 'Sem restrições configuradas', icon: Globe },
      ].map((s, i) => (
        <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><s.icon className="h-5 w-5" /></div>
          <div className="flex-1"><p className="font-semibold text-sm">{s.title}</p><p className="text-xs text-neutral-500">{s.description}</p></div>
          <Button size="sm" variant="outline">Configurar</Button>
        </div>
      ))}
    </div>}
  />;
}

export function Integracoes() {
  const list = [
    { name: 'SEFAZ', kind: 'Fiscal', status: 'Conectado', tone: 'green' },
    { name: 'Google Maps', kind: 'Geolocalização', status: 'Conectado', tone: 'green' },
    { name: 'Sicredi Open Banking', kind: 'Financeiro', status: 'Conectado', tone: 'green' },
    { name: 'Twilio SMS', kind: 'Mensageria', status: 'Desconectado', tone: 'red' },
    { name: 'SendGrid', kind: 'E-mail', status: 'Conectado', tone: 'green' },
    { name: 'CNH-e (Governo)', kind: 'Documentos', status: 'Homologação', tone: 'amber' },
  ];
  return <MockPageShell title="Integrações" breadcrumbs={bcA('Integrações')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{list.map((it, i) => (
      <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><Plug className="h-5 w-5" /></div>
            <div><h3 className="font-semibold text-sm">{it.name}</h3><p className="text-[11px] text-neutral-500">{it.kind}</p></div>
          </div>
          <StatusBadge value={it.status} tone={it.tone} />
        </div>
        <Button size="sm" variant="outline" className="mt-3 w-full">Configurar</Button>
      </div>
    ))}</div>}
  />;
}

export function AdminAPI() {
  const keys = [
    { name: 'Aplicativo Motorista', key: 'sk_live_...4a2b', created: '10/01/2026', last: 'Hoje', scope: 'read+write' },
    { name: 'Integração ERP Cliente', key: 'sk_live_...9x1c', created: '02/12/2025', last: '2 dias', scope: 'read' },
    { name: 'BI Metabase', key: 'sk_live_...7z3d', created: '15/11/2025', last: 'Ontem', scope: 'read' },
  ];
  return <MockPageShell title="API Keys" breadcrumbs={bcA('API')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova chave</Button>}
    table={<MockTable columns={[{ key: 'name', label: 'Nome' }, { key: 'key', label: 'Chave', mono: true }, { key: 'created', label: 'Criada' }, { key: 'last', label: 'Último uso' }, { key: 'scope', label: 'Escopo' }]} rows={keys} />}
  />;
}

export function Webhooks() {
  const rows = [
    { name: 'Nova OS', url: 'https://client.com/hooks/os', events: 'os.created, os.status_change', status: 'Ativo', last: '2h atrás' },
    { name: 'Nova NF', url: 'https://contabil.parceiro/nf', events: 'nf.emitted', status: 'Ativo', last: '1d atrás' },
    { name: 'Alerta preventiva', url: 'https://slack.com/webhooks/...', events: 'preventive.overdue', status: 'Falha', last: '3d atrás' },
  ];
  return <MockPageShell title="Webhooks" breadcrumbs={bcA('Webhooks')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Novo webhook</Button>}
    table={<MockTable columns={[{ key: 'name', label: 'Nome' }, { key: 'url', label: 'URL', mono: true }, { key: 'events', label: 'Eventos' }, { key: 'status', label: 'Status', status: true }, { key: 'last', label: 'Último' }]} rows={rows} statusMap={{ Ativo: 'green', Falha: 'red' }} />}
  />;
}

export function AdminBackup() {
  const rows = [
    { date: '10/02/2026 03:00', kind: 'Automático', size: '842 MB', duration: '4min 22s', status: 'Sucesso' },
    { date: '09/02/2026 03:00', kind: 'Automático', size: '839 MB', duration: '4min 08s', status: 'Sucesso' },
    { date: '08/02/2026 03:00', kind: 'Automático', size: '836 MB', duration: '4min 18s', status: 'Sucesso' },
    { date: '07/02/2026 15:22', kind: 'Manual', size: '834 MB', duration: '4min 45s', status: 'Sucesso' },
  ];
  return <MockPageShell title="Backup" breadcrumbs={bcA('Backup')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white"><CloudUpload className="h-4 w-4 mr-2" />Backup agora</Button>}
    kpis={[
      <MockKpi key="1" icon={CloudUpload} label="Último backup" value="03:00" sub="Sucesso" tone="success" />,
      <MockKpi key="2" icon={Database} label="Retenção" value="30 dias" />,
      <MockKpi key="3" icon={Server} label="Armazenamento" value="24.6 GB" />,
    ]}
    table={<MockTable columns={[{ key: 'date', label: 'Data', mono: true }, { key: 'kind', label: 'Tipo' }, { key: 'size', label: 'Tamanho', align: 'right' }, { key: 'duration', label: 'Duração' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Sucesso: 'green', Falha: 'red' }} />}
  />;
}

export function AdminNotificacoes() {
  const rows = [
    { channel: 'E-mail (SendGrid)', events: 'OS finalizada, NF emitida', status: 'Ativo' },
    { channel: 'SMS (Twilio)', events: 'Preventiva urgente', status: 'Desativado' },
    { channel: 'WhatsApp', events: 'Confirmação de viagem', status: 'Homologação' },
    { channel: 'Push (mobile)', events: 'Motorista - Nova escala', status: 'Ativo' },
  ];
  return <MockPageShell title="Notificações" breadcrumbs={bcA('Notificações')}
    table={<MockTable columns={[{ key: 'channel', label: 'Canal' }, { key: 'events', label: 'Eventos' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativo: 'green', Desativado: 'neutral', Homologação: 'amber' }} />}
  />;
}

export function Multiempresa() {
  const rows = [
    { code: 'EMP-01', name: 'Dany Trans Transportes LTDA', cnpj: '12.345.678/0001-90', users: 84, status: 'Ativa' },
    { code: 'EMP-02', name: 'Dany Trans Turismo LTDA', cnpj: '12.345.678/0002-70', users: 22, status: 'Ativa' },
    { code: 'EMP-03', name: 'Dany Trans Filial Litoral', cnpj: '12.345.678/0003-51', users: 15, status: 'Configurando' },
  ];
  return <MockPageShell title="Multiempresa" subtitle="Gestão de múltiplas empresas / filiais" breadcrumbs={bcA('Multiempresa')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova empresa</Button>}
    table={<MockTable columns={[{ key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Razão social' }, { key: 'cnpj', label: 'CNPJ', mono: true }, { key: 'users', label: 'Usuários', align: 'right' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativa: 'green', Configurando: 'amber' }} />}
  />;
}

export function LGPD() {
  return <MockPageShell title="LGPD — Proteção de Dados" breadcrumbs={bcA('LGPD')}
    extra={<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[
        { title: 'Titulares registrados', description: '128 funcionários + 2.140 clientes', icon: ShieldCheck },
        { title: 'Solicitações de acesso (LGPD)', description: '2 pendentes', icon: ShieldAlert },
        { title: 'Consentimentos ativos', description: '2.128 registros', icon: KeyRound },
        { title: 'DPO — Encarregado', description: 'Ricardo Bastos', icon: ShieldCheck },
        { title: 'Retenção de dados', description: 'Ex-funcionários: 5 anos • Clientes inativos: 3 anos', icon: Database },
        { title: 'Incidentes reportados', description: 'Nenhum nos últimos 12 meses', icon: ShieldCheck },
      ].map((s, i) => (
        <div key={i} className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><s.icon className="h-5 w-5" /></div>
          <div className="flex-1"><p className="font-semibold text-sm">{s.title}</p><p className="text-xs text-neutral-500">{s.description}</p></div>
        </div>
      ))}
    </div>}
  />;
}
